import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDownIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { atnaHowCopy as c, type Bi } from '../../data/atnaHow';
import { IntentBubble, AtnaOrb, StepsRow } from './how/HowJourney';
import { TrustStatement, Comparison, TamePanel } from './how/HowProof';
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 12
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.3,
    ease: EASE
  }
};
export function AtnaHow() {
  const {
    lang
  } = useLanguage();
  const pick = (b: Bi) => lang === 'FR' ? b.fr : b.en;
  return <section className="relative overflow-hidden bg-[#FDFDFF] py-24 lg:py-28 px-6" aria-labelledby="atna-how-title" data-id="element-953" data-mp="22dztnxcpvfq1">
      <div className="pointer-events-none absolute top-0 right-[10%] w-[760px] h-[480px] rounded-full bg-[#EEEDFD] opacity-70 blur-[150px]" aria-hidden="true" data-id="element-954" data-mp="3htqk93f9ni8r" />

      <div className="relative max-w-[1520px] mx-auto" data-id="element-955" data-mp="3dv9furn8clv1">
        {/* TOP: message (left) + one continuous journey (right) */}
        <div className="grid grid-cols-1 xl:grid-cols-[27fr_73fr] gap-14 xl:gap-12 items-start" data-id="element-956" data-mp="1r0e7bcux34e">
          <motion.div {...reveal} className="min-w-0 xl:pt-4" data-id="element-957" data-mp="5slpwtdnnbd0">
            <p className="font-sans text-[13px] md:text-[14px] font-medium tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-958" data-mp="5r0wx86zo0h9">
              {pick(c.eyebrow)}
            </p>
            <h2 id="atna-how-title" className="font-serif text-[2.2rem] sm:text-[2.8rem] lg:text-[3.1rem] leading-[1.02] tracking-tight mt-5" data-id="element-959" data-mp="3a8mqgag9jeeo">
              {c.headline.map(l => <span key={l.text.fr} className={`block ${l.accent ? 'text-[#4F46E5]' : 'text-[#121A3A]'}`} data-id="element-960" data-mp="3j6ysd8mkxymf">
                  {pick(l.text)}
                </span>)}
            </h2>
            <p className="font-sans text-[15.5px] md:text-[16px] text-[#4A5268] leading-relaxed mt-5 max-w-[440px]" data-id="element-961" data-mp="2qib2ezqvyl2j">
              {pick(c.body)}
            </p>
          </motion.div>

          <motion.div {...reveal} className="min-w-0" data-id="element-962" data-mp="2nfa22q386gg">
            {/* Desktop: question → ATNA → steps, all in one system */}
            <div className="hidden xl:block relative h-[168px]" data-id="element-963" data-mp="3ttgswxgao4cn">
              <svg className="absolute inset-0 w-full h-full overflow-visible" viewBox="0 0 1000 188" preserveAspectRatio="none" aria-hidden="true" data-id="element-964" data-mp="bgsea0tlquhu">
                {/* question → ATNA */}
                <path d="M350 60 C 440 60, 480 84, 548 84" fill="none" stroke="#A9A4F2" strokeWidth="1.5" vectorEffect="non-scaling-stroke" data-id="element-965" data-mp="3fwbpl57ytjl3" />
                {/* ATNA → step 1 */}
                <path d="M600 158 C 420 158, 110 144, 72 168" fill="none" stroke="#A9A4F2" strokeWidth="1.5" vectorEffect="non-scaling-stroke" data-id="element-966" data-mp="3owgn8nncx3m2" />
                {/* ATNA → step 6 */}
                <path d="M700 84 C 880 84, 930 116, 940 168" fill="none" stroke="#A9A4F2" strokeWidth="1.5" vectorEffect="non-scaling-stroke" data-id="element-967" data-mp="143mz4yubzvvu" />
              </svg>
              <span className="absolute left-[7.2%] bottom-0 -translate-x-1/2 translate-y-1/2 w-2 h-2 rounded-full bg-[#6D66F0]" aria-hidden="true" data-id="element-968" data-mp="17mca7l6j2kx6" />
              <span className="absolute left-[94%] bottom-0 -translate-x-1/2 translate-y-1/2 w-2 h-2 rounded-full bg-[#6D66F0]" aria-hidden="true" data-id="element-969" data-mp="1bps3q8naw55n" />
              <div className="absolute left-[3%] top-[34px]" data-id="element-970" data-mp="12n91nqrc9wkg">
                <IntentBubble pick={pick} data-id="element-971" data-mp="12pvbjx1pco5u" />
              </div>
              <div className="absolute left-[54.5%] top-0" data-id="element-972" data-mp="1qeo91xxz342k">
                <AtnaOrb pick={pick} data-id="element-973" data-mp="1vc6ma0j0w5gd" />
              </div>
            </div>

            {/* Tablet & mobile: stacked */}
            <div className="xl:hidden flex flex-col items-center gap-3 mb-3" data-id="element-974" data-mp="1gobpo91w9gy0">
              <IntentBubble pick={pick} data-id="element-975" data-mp="1lmdbv2um9wdp" />
              <ArrowDownIcon className="w-5 h-5 text-[#6D66F0]" strokeWidth={1.6} aria-hidden="true" data-id="element-976" data-mp="1llbuyhqx6fo2" />
              <AtnaOrb pick={pick} size="sm" data-id="element-977" data-mp="29amrfn18lnwe" />
              <ArrowDownIcon className="w-5 h-5 text-[#6D66F0]" strokeWidth={1.6} aria-hidden="true" data-id="element-978" data-mp="2e2nymujjsofk" />
            </div>

            <div className="xl:mt-4" data-id="element-979" data-mp="phd6cvi4fiuc">
              <StepsRow pick={pick} data-id="element-980" data-mp="pifw303pjf8o" />
            </div>
          </motion.div>
        </div>

        {/* BOTTOM: why ATNA can be trusted to act */}
        <motion.div {...reveal} className="mt-16 grid grid-cols-1 xl:grid-cols-[30fr_47fr_23fr] gap-5 items-stretch" data-id="element-981" data-mp="gg6qmonaqc25">
          <TrustStatement pick={pick} data-id="element-982" data-mp="biws8jhxiq26" />
          <Comparison pick={pick} data-id="element-983" data-mp="19541w7t3b8le" />
          <TamePanel pick={pick} data-id="element-984" data-mp="1942hvdu3jbrj" />
        </motion.div>
      </div>
    </section>;
}